import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, MapPin, Navigation, Clock, Star, CreditCard, Check, X, Download, Loader2, RefreshCcw, FileText } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

const RideHistory = () => {
  const { t } = useTranslation();
  const { user, api } = useAuth();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [refundDialog, setRefundDialog] = useState({ open: false, ride: null });
  const [refundReason, setRefundReason] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await api.get('/rides/history/me');
      setRides(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const response = await api.get('/rides/history/export-pdf', {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `stationcab_historique_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF téléchargé !');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  const downloadInvoice = async (rideId) => {
    try {
      const response = await api.get(`/rides/${rideId}/invoice/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `facture_${rideId.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Facture téléchargée !');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Erreur lors du téléchargement de la facture');
    }
  };

  const openRefundDialog = (ride) => {
    setRefundDialog({ open: true, ride });
    setRefundReason('');
  };

  const submitRefund = async () => {
    if (!refundReason.trim()) {
      toast.error('Veuillez indiquer le motif du remboursement');
      return;
    }
    
    setRefundLoading(true);
    try {
      await api.post(`/rides/${refundDialog.ride.id}/refund`, {
        reason: refundReason
      });
      
      toast.success('Demande de remboursement envoyée');
      setRefundDialog({ open: false, ride: null });
      fetchHistory(); // Refresh to show updated status
    } catch (error) {
      console.error('Error requesting refund:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la demande');
    } finally {
      setRefundLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status, paymentStatus, refundStatus) => {
    if (refundStatus === 'pending') {
      return (
        <span className="flex items-center gap-1 text-sm text-amber-500">
          <RefreshCcw className="w-4 h-4" /> Remboursement en cours
        </span>
      );
    }
    if (refundStatus === 'refunded') {
      return (
        <span className="flex items-center gap-1 text-sm text-green-500">
          <Check className="w-4 h-4" /> Remboursée
        </span>
      );
    }
    if (status === 'cancelled') {
      return (
        <span className="flex items-center gap-1 text-sm text-destructive">
          <X className="w-4 h-4" /> Annulée
        </span>
      );
    }
    if (status === 'completed') {
      if (paymentStatus === 'paid' || paymentStatus === 'completed') {
        return (
          <span className="flex items-center gap-1 text-sm text-green-500">
            <Check className="w-4 h-4" /> Payée
          </span>
        );
      }
      return (
        <span className="flex items-center gap-1 text-sm text-primary">
          <CreditCard className="w-4 h-4" /> En attente
        </span>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={user?.role === 'driver' ? '/driver' : '/passenger'}>
              <Button variant="ghost" size="icon" data-testid="back-btn" className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <h1 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Historique</h1>
          </div>
          {rides.length > 0 && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={exportPDF}
              disabled={exporting}
              className="gap-2"
              data-testid="export-pdf-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              PDF
            </Button>
          )}
        </div>
      </header>

      {/* Content */}
      <div className="pt-24 pb-8 px-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : rides.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">Aucune course</h3>
            <p className="text-muted-foreground">Votre historique de courses apparaîtra ici</p>
          </div>
        ) : (
          <div className="space-y-4">
            {rides.map((ride) => (
              <Card key={ride.id} className="bg-card border-border/50">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {formatDate(ride.created_at)}
                      </p>
                      {user?.role === 'passenger' && ride.driver_name && (
                        <p className="font-medium">{ride.driver_name}</p>
                      )}
                      {user?.role === 'driver' && (
                        <p className="font-medium">{ride.passenger_name}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-primary">
                        {ride.final_fare || ride.estimated_fare}€
                      </p>
                      {getStatusBadge(ride.status, ride.payment_status, ride.refund_status)}
                    </div>
                  </div>
                  
                  <div className="space-y-2 pt-2 border-t border-border">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-muted-foreground">{ride.pickup.address}</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <Navigation className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-muted-foreground">{ride.destination.address}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-2 text-sm text-muted-foreground">
                    <span>{ride.distance_km} km</span>
                    
                    {/* Actions for completed rides */}
                    {ride.status === 'completed' && (
                      <div className="flex items-center gap-2">
                        {/* Invoice button */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadInvoice(ride.id)}
                          className="gap-1 h-8"
                          data-testid={`invoice-btn-${ride.id}`}
                        >
                          <FileText className="w-3 h-3" />
                          Facture
                        </Button>
                        
                        {/* Refund button - only if paid and not already refunded */}
                        {(ride.payment_status === 'paid' || ride.payment_status === 'completed') && 
                         !ride.refund_status && user?.role === 'passenger' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openRefundDialog(ride)}
                            className="gap-1 h-8 text-amber-500 border-amber-500/30 hover:bg-amber-500/10"
                            data-testid={`refund-btn-${ride.id}`}
                          >
                            <RefreshCcw className="w-3 h-3" />
                            Remboursement
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      
      {/* Refund Dialog */}
      <Dialog open={refundDialog.open} onOpenChange={(open) => !open && setRefundDialog({ open: false, ride: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Demande de remboursement</DialogTitle>
            <DialogDescription>
              Vous demandez un remboursement pour la course du {refundDialog.ride && formatDate(refundDialog.ride.created_at)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-muted/30 rounded-lg p-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Montant</span>
                <span className="font-bold text-lg text-primary">
                  {refundDialog.ride?.final_fare || refundDialog.ride?.estimated_fare}€
                </span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="refund-reason">Motif du remboursement *</Label>
              <Textarea
                id="refund-reason"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder="Décrivez la raison de votre demande de remboursement..."
                className="min-h-[100px]"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRefundDialog({ open: false, ride: null })}
            >
              Annuler
            </Button>
            <Button
              onClick={submitRefund}
              disabled={refundLoading || !refundReason.trim()}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {refundLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Envoyer la demande'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RideHistory;
